'use client';

import { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Props {
  open: boolean;
  onClose: () => void;
}

const MANUFACTURERS = ['GAF', 'CertainTeed', 'Owens Corning', 'Tamko', 'IKO', 'Other'] as const;
const REBATE_TYPES = ['Volume Rebate', 'Promotional', 'Tier Bonus', 'Per-Job', 'Other'] as const;

interface FormData {
  manufacturer: typeof MANUFACTURERS[number];
  rebateType:   typeof REBATE_TYPES[number];
  period: string;
  jobId: string;
  amountExpected: string;  // string for input handling, parsed at submit
  amountReceived: string;
  overrideToOverdue: boolean;
  dateClaimed: string;
  dateReceived: string;
  notes: string;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

const INITIAL_FORM: FormData = {
  manufacturer: 'GAF',
  rebateType: 'Volume Rebate',
  period: '',
  jobId: '',
  amountExpected: '',
  amountReceived: '0',
  overrideToOverdue: false,
  dateClaimed: todayIso(),
  dateReceived: '',
  notes: '',
};

type FieldErrors = Partial<Record<keyof FormData, string>>;

function validate(data: FormData): FieldErrors {
  const errors: FieldErrors = {};
  if (!data.period.trim()) errors.period = 'Period is required (e.g., "Q1 2026")';
  if (data.rebateType === 'Per-Job' && !data.jobId.trim()) errors.jobId = 'Job ID is required for Per-Job rebates';
  const expected = parseFloat(data.amountExpected);
  if (!Number.isFinite(expected) || expected <= 0) errors.amountExpected = 'Amount expected must be > 0';
  const received = parseFloat(data.amountReceived);
  if (!Number.isFinite(received) || received < 0) errors.amountReceived = 'Amount received cannot be negative';
  if (Number.isFinite(expected) && Number.isFinite(received) && received > expected) {
    errors.amountReceived = 'Amount received cannot exceed amount expected';
  }
  if (!data.dateClaimed) errors.dateClaimed = 'Date claimed is required';
  if (received > 0 && !data.dateReceived) errors.dateReceived = 'Date received is required when amount > 0';
  return errors;
}

// Derived status: status is computed from amounts and the override checkbox.
// Keeps the form opinionated — bookkeeper can't accidentally mark a $0-received
// rebate as 'Received'.
function deriveStatus(data: FormData): 'expected' | 'partial' | 'received' | 'overdue' {
  if (data.overrideToOverdue) return 'overdue';
  const expected = parseFloat(data.amountExpected) || 0;
  const received = parseFloat(data.amountReceived) || 0;
  if (received >= expected && expected > 0) return 'received';
  if (received > 0) return 'partial';
  return 'expected';
}

const STATUS_LABEL: Record<ReturnType<typeof deriveStatus>, string> = {
  expected: 'Expected',
  partial:  'Partial',
  received: 'Received',
  overdue:  'Overdue',
};

const LOG = '[Financial:Rebates]';

export default function AddRebateDrawer({ open, onClose }: Props) {
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [showSuccess, setShowSuccess] = useState(false);

  // Reset whenever drawer reopens.
  useEffect(() => {
    if (open) {
      setFormData(INITIAL_FORM);
      setErrors({});
      setShowSuccess(false);
    }
  }, [open]);

  const update = <K extends keyof FormData>(key: K) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const value = e.target.type === 'checkbox'
        ? (e.target as HTMLInputElement).checked
        : e.target.value;
      setFormData((prev) => {
        const next = { ...prev, [key]: value } as FormData;
        // If rebate type changes away from Per-Job, clear the jobId so stale
        // state doesn't leak through. (Per Mike's spec — keep field unmounted-
        // equivalent state.)
        if (key === 'rebateType' && value !== 'Per-Job') {
          next.jobId = '';
        }
        // If amount received drops to 0, clear the date_received so we don't
        // leave orphan dates.
        if (key === 'amountReceived') {
          const received = parseFloat(value as string);
          if (!Number.isFinite(received) || received <= 0) {
            next.dateReceived = '';
          }
        }
        return next;
      });
      setErrors((prev) => ({ ...prev, [key]: undefined }));
    };

  const receivedNum = parseFloat(formData.amountReceived);
  const receivedIsPositive = Number.isFinite(receivedNum) && receivedNum > 0;
  const isPerJob = formData.rebateType === 'Per-Job';
  const derivedStatus = deriveStatus(formData);

  const handleSave = () => {
    const found = validate(formData);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }
    console.log(`${LOG} Add Rebate submit (demo, not persisted):`, {
      ...formData,
      derivedStatus,
    });
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      onClose();
    }, 2000);
  };

  if (!open) return null;

  return (
    // top-[60px] + z-30 = below the app top bar (z-header) — same defensive
    // choice as the Installer Network AddNewCrewDrawer and the crew detail
    // drawer. Top bar stays sovereign.
    <div className="fixed top-[60px] left-0 right-0 bottom-0 z-30 flex justify-end">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-[520px] h-full bg-white shadow-2xl flex flex-col overflow-hidden">

        {/* Header */}
        <div
          className="flex-shrink-0 px-6 py-5 border-b border-gray-100"
          style={{ background: 'linear-gradient(135deg, #000150 0%, #033494 100%)' }}
        >
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-white">Add Rebate</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-all"
              suppressHydrationWarning
            >
              <Icon name="XMarkIcon" size={18} />
            </button>
          </div>
        </div>

        {/* Demo-mode banner */}
        <div
          className="flex-shrink-0 px-6 py-2.5 border-b border-amber-200"
          style={{ background: '#FEF3C7', color: '#92400E' }}
        >
          <p className="text-[11px] font-semibold flex items-center gap-1.5">
            <Icon name="ExclamationTriangleIcon" size={13} />
            DEMO MODE — submissions are not saved. Real persistence is dev-team backlog work.
          </p>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {showSuccess && (
            <div
              className="rounded-lg px-3 py-2.5 flex items-center gap-2"
              style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.25)' }}
            >
              <Icon name="CheckCircleIcon" size={16} style={{ color: '#10B981' }} />
              <p className="text-xs font-semibold" style={{ color: '#047857' }}>
                Rebate saved (demo) — closing drawer…
              </p>
            </div>
          )}

          <FormField label="Manufacturer" required>
            <select
              value={formData.manufacturer}
              onChange={update('manufacturer')}
              suppressHydrationWarning
              className="w-full px-3 py-2 text-xs rounded-lg border border-gray-200 bg-white focus:outline-none focus:border-[#07B0E9] focus:ring-1 focus:ring-[#07B0E9]/30 transition-all"
            >
              {MANUFACTURERS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </FormField>

          <FormField label="Rebate Type" required>
            <select
              value={formData.rebateType}
              onChange={update('rebateType')}
              suppressHydrationWarning
              className="w-full px-3 py-2 text-xs rounded-lg border border-gray-200 bg-white focus:outline-none focus:border-[#07B0E9] focus:ring-1 focus:ring-[#07B0E9]/30 transition-all"
            >
              {REBATE_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </FormField>

          <FormField label="Period" required error={errors.period}>
            <input
              type="text"
              value={formData.period}
              onChange={update('period')}
              suppressHydrationWarning
              className="w-full px-3 py-2 text-xs rounded-lg border border-gray-200 focus:outline-none focus:border-[#07B0E9] focus:ring-1 focus:ring-[#07B0E9]/30 transition-all"
              placeholder='e.g., "Q1 2026" or "Mar 2026 promo"'
            />
          </FormField>

          {/* Job ID — conditionally rendered (not just hidden) so stale state
              never accompanies a non-Per-Job rebate. */}
          {isPerJob && (
            <FormField label="Job ID" required error={errors.jobId}>
              <input
                type="text"
                value={formData.jobId}
                onChange={update('jobId')}
                suppressHydrationWarning
                className="w-full px-3 py-2 text-xs rounded-lg border border-gray-200 focus:outline-none focus:border-[#07B0E9] focus:ring-1 focus:ring-[#07B0E9]/30 transition-all"
                placeholder="e.g., J-1023"
              />
            </FormField>
          )}

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Amount Expected ($)" required error={errors.amountExpected}>
              <input
                type="number"
                min={0}
                step={1}
                value={formData.amountExpected}
                onChange={update('amountExpected')}
                suppressHydrationWarning
                className="w-full px-3 py-2 text-xs rounded-lg border border-gray-200 focus:outline-none focus:border-[#07B0E9] focus:ring-1 focus:ring-[#07B0E9]/30 transition-all"
                placeholder="0"
              />
            </FormField>
            <FormField label="Amount Received ($)" error={errors.amountReceived}>
              <input
                type="number"
                min={0}
                step={1}
                value={formData.amountReceived}
                onChange={update('amountReceived')}
                suppressHydrationWarning
                className="w-full px-3 py-2 text-xs rounded-lg border border-gray-200 focus:outline-none focus:border-[#07B0E9] focus:ring-1 focus:ring-[#07B0E9]/30 transition-all"
                placeholder="0"
              />
            </FormField>
          </div>

          {/* Derived status display + override-to-overdue checkbox */}
          <div
            className="rounded-lg px-3 py-2.5"
            style={{ background: 'rgba(248,250,252,0.7)', border: '1px solid #F3F4F6' }}
          >
            <p className="text-[11px] font-semibold text-gray-600">
              Status: <span style={{ color: '#000150' }}>{STATUS_LABEL[derivedStatus]} (auto)</span>
            </p>
            <label className="flex items-center gap-2 mt-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.overrideToOverdue}
                onChange={update('overrideToOverdue')}
                suppressHydrationWarning
                className="w-3.5 h-3.5 rounded border-gray-300 text-[#EF4444] focus:ring-[#EF4444]/30"
              />
              <span className="text-[11px] text-gray-700">Override to Overdue</span>
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Date Claimed" required error={errors.dateClaimed}>
              <input
                type="date"
                value={formData.dateClaimed}
                onChange={update('dateClaimed')}
                suppressHydrationWarning
                className="w-full px-3 py-2 text-xs rounded-lg border border-gray-200 focus:outline-none focus:border-[#07B0E9] focus:ring-1 focus:ring-[#07B0E9]/30 transition-all"
              />
            </FormField>
            <FormField label="Date Received" error={errors.dateReceived}>
              <input
                type="date"
                value={formData.dateReceived}
                onChange={update('dateReceived')}
                disabled={!receivedIsPositive}
                suppressHydrationWarning
                className="w-full px-3 py-2 text-xs rounded-lg border border-gray-200 focus:outline-none focus:border-[#07B0E9] focus:ring-1 focus:ring-[#07B0E9]/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-50"
              />
            </FormField>
          </div>

          <FormField label="Notes">
            <textarea
              value={formData.notes}
              onChange={update('notes')}
              suppressHydrationWarning
              rows={3}
              className="w-full px-3 py-2 text-xs rounded-lg border border-gray-200 focus:outline-none focus:border-[#07B0E9] focus:ring-1 focus:ring-[#07B0E9]/30 transition-all resize-none"
              placeholder="Optional context, claim reference, notes…"
            />
          </FormField>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-100 transition-all"
            suppressHydrationWarning
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={showSuccess}
            className="px-4 py-2 rounded-lg text-xs font-semibold text-white hover:opacity-90 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ background: '#07B0E9' }}
            suppressHydrationWarning
          >
            Save Rebate
          </button>
        </div>
      </div>
    </div>
  );
}

function FormField({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider font-semibold text-gray-500 mb-1">
        {label}{required && <span style={{ color: '#EF4444' }}> *</span>}
      </label>
      {children}
      {error && (
        <p className="text-[11px] mt-1" style={{ color: '#B91C1C' }}>{error}</p>
      )}
    </div>
  );
}
